import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Download, Settings, AlertCircle, TrendingUp, TrendingDown, FileText } from "lucide-react";


const fmtZar = (v) => {
  if (v === null || v === undefined) return "—";
  const sign = v < 0 ? "-" : "";
  return `${sign}R ${Math.abs(v).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v.toFixed(1)}%`);
const nowFy = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return { year: y, month: m === 1 ? 12 : m - 1, fyLabel: m >= 3 ? `${y}/${(y + 1).toString().slice(2)}` : `${y - 1}/${y.toString().slice(2)}` };
};
const monthName = (y, m) => new Date(y, m - 1, 1).toLocaleString("en-ZA", { month: "long", year: "numeric" });


export default function MonthlyReport() {
  const { user } = useAuth();
  const isOwner = user?.role === "Owner";
  const seed = nowFy();
  const [year, setYear]   = useState(seed.year);
  const [month, setMonth] = useState(seed.month);
  const [report, setReport] = useState(null);
  const [aging, setAging] = useState(null);   // loaded separately (slow)
  const [loading, setLoading] = useState(false);
  const [agingLoading, setAgingLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError(""); setReport(null); setAging(null);
    try {
      const { data } = await api.get(`/monthly-report?year=${year}&month=${month}`);
      setReport(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
    // Aging in parallel — don't block the main report
    setAgingLoading(true);
    try {
      const { data } = await api.get(`/monthly-report/aging?year=${year}&month=${month}`);
      setAging(data);
    } catch (e) { /* aging errors are surfaced inline in the card */ }
    finally { setAgingLoading(false); }
  };

  useEffect(() => { if (isOwner) load(); }, [year, month, isOwner]);   // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = (style) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/monthly-report/pdf?year=${year}&month=${month}&style=${style}`;
    // Trigger download in a new tab so the browser handles the response headers
    window.open(url, "_blank");
  };

  if (!isOwner) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="mr-not-owner">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
        <h2 className="font-display text-2xl">Owner-only page</h2>
        <p className="text-muted-foreground mt-2">Only the Owner can view the Monthly Report.</p>
      </div>
    );
  }

  const monthLabel = monthName(year, month);
  const pyLabel    = monthName(year - 1, month);

  return (
    <div className="space-y-6" data-testid="mr-page">
      <PageHeader
        eyebrow="Bookkeeping"
        title="Monthly Report"
        subtitle={`Owner overview · SA FY runs Mar–Feb · Currently viewing ${monthLabel}`}
        actions={
          <div className="flex gap-2 flex-wrap">
            <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
            <Link to="/monthly-report/setup" className="btn-secondary inline-flex items-center gap-2" data-testid="mr-open-setup">
              <Settings className="w-4 h-4" /> Setup
            </Link>
            <button className="btn-secondary inline-flex items-center gap-2" onClick={() => downloadPdf("printable")} disabled={loading} data-testid="mr-download-print">
              <FileText className="w-4 h-4" /> Print-friendly PDF
            </button>
            <button className="btn-primary inline-flex items-center gap-2" onClick={() => downloadPdf("branded")} disabled={loading} data-testid="mr-download-branded">
              <Download className="w-4 h-4" /> Branded PDF
            </button>
          </div>
        }
      />

      {error && (
        <div className="border border-destructive/40 bg-destructive/5 text-destructive rounded p-3 text-sm flex items-center gap-2" data-testid="mr-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading && <p className="text-sm text-muted-foreground">Loading Zoho data…</p>}

      {report && (
        <>
          <RevenueBlock rev={report.revenue} monthLabel={monthLabel} pyLabel={pyLabel} />
          <PnlBlock lines={report.pnl_lines} monthLabel={monthLabel} pyLabel={pyLabel} />
          <SalesBlock sales={report.sales} monthLabel={monthLabel} />
          <ManufacturingBlock mfg={report.manufacturing_cost} monthLabel={monthLabel} />
          <AgingBlock aging={aging} loading={agingLoading} monthLabel={monthLabel} />
          <CashFlowBlock cf={report.cash_flow} monthLabel={monthLabel} />
          <VatBlock vat={report.vat} monthLabel={monthLabel} />
        </>
      )}
    </div>
  );
}


function MonthPicker({ year, month, onChange }) {
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return (
    <div className="flex gap-1 items-center border rounded" data-testid="mr-month-picker">
      <select value={month} onChange={(e) => onChange(year, parseInt(e.target.value))}
              className="bg-transparent px-2 py-1.5 text-sm border-r outline-none" data-testid="mr-month-select">
        {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
      </select>
      <select value={year} onChange={(e) => onChange(parseInt(e.target.value), month)}
              className="bg-transparent px-2 py-1.5 text-sm outline-none" data-testid="mr-year-select">
        {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i).map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}


function RevenueBlock({ rev, monthLabel, pyLabel }) {
  const attainmentTone = rev.attainment_pct == null ? "text-muted-foreground" : rev.attainment_pct >= 100 ? "text-green-700" : "text-destructive";
  return (
    <section className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="mr-revenue-block">
      <KpiCard label={`Revenue · ${monthLabel}`}  value={fmtZar(rev.current)} testId="mr-rev-current" />
      <KpiCard label={`Prior Yr · ${pyLabel}`}    value={fmtZar(rev.prior_year_same_month)} testId="mr-rev-py" />
      <KpiCard label={`Target (${rev.growth_target_pct?.toFixed?.(0) ?? 0}% growth)`} value={fmtZar(rev.target_amount)} testId="mr-rev-target" />
      <KpiCard label="Attainment" value={fmtPct(rev.attainment_pct)} tone={attainmentTone} testId="mr-rev-attain" />
    </section>
  );
}


function PnlBlock({ lines, monthLabel, pyLabel }) {
  if (!lines || lines.length === 0) {
    return (
      <Card title="Profit & Loss — Selected Lines" testId="mr-pnl-block">
        <p className="text-sm text-muted-foreground">
          No accounts picked yet — go to <Link to="/monthly-report/setup" className="underline text-primary">Setup</Link> to choose which P&amp;L lines appear here.
        </p>
      </Card>
    );
  }
  return (
    <Card title="Profit & Loss — Selected Lines" testId="mr-pnl-block">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground uppercase tracking-wider border-b">
            <tr>
              <th className="text-left py-2">Account</th>
              <th className="text-right py-2">{monthLabel}</th>
              <th className="text-right py-2">{pyLabel}</th>
              <th className="text-right py-2">YTD</th>
              <th className="text-right py-2">Prior YTD</th>
              <th className="text-right py-2">Target %</th>
              <th className="text-right py-2">Target R</th>
              <th className="text-right py-2">Variance</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.account_id} className="border-b" data-testid={`mr-pnl-row-${l.account_id}`}>
                <td className="py-2">{l.account_name}</td>
                <td className="py-2 text-right tabular-nums">{fmtZar(l.current)}</td>
                <td className="py-2 text-right tabular-nums text-muted-foreground">{fmtZar(l.prior_year_same_month)}</td>
                <td className="py-2 text-right tabular-nums">{fmtZar(l.ytd)}</td>
                <td className="py-2 text-right tabular-nums text-muted-foreground">{fmtZar(l.prior_year_ytd)}</td>
                <td className="py-2 text-right tabular-nums">{fmtPct(l.target_pct)}</td>
                <td className="py-2 text-right tabular-nums">{fmtZar(l.target_amount)}</td>
                <td className={`py-2 text-right tabular-nums font-medium ${l.variance_vs_target < 0 ? "text-destructive" : "text-green-700"}`}>
                  {fmtZar(l.variance_vs_target)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


function SalesBlock({ sales, monthLabel }) {
  const m = sales.month || {}, y = sales.ytd || {};
  return (
    <Card title="Sales Targets — Roasted kg" testId="mr-sales-block">
      <table className="w-full text-sm">
        <thead className="text-xs text-muted-foreground uppercase tracking-wider border-b">
          <tr><th className="text-left py-2">Period</th><th className="text-right py-2">Actual (kg)</th><th className="text-right py-2">Target (kg)</th><th className="text-right py-2">Attainment</th></tr>
        </thead>
        <tbody>
          <tr className="border-b"><td className="py-2">{monthLabel}</td><td className="py-2 text-right tabular-nums">{m.kg_roasted?.toLocaleString?.("en-ZA") ?? "—"}</td><td className="py-2 text-right tabular-nums">{m.target_kg?.toLocaleString?.("en-ZA") ?? "—"}</td><td className="py-2 text-right tabular-nums">{fmtPct(m.attainment_pct)}</td></tr>
          <tr><td className="py-2">YTD</td><td className="py-2 text-right tabular-nums">{y.kg_roasted?.toLocaleString?.("en-ZA") ?? "—"}</td><td className="py-2 text-right tabular-nums">{y.target_kg?.toLocaleString?.("en-ZA") ?? "—"}</td><td className="py-2 text-right tabular-nums">{fmtPct(y.attainment_pct)}</td></tr>
        </tbody>
      </table>
    </Card>
  );
}


function ManufacturingBlock({ mfg, monthLabel }) {
  if (!mfg) return null;
  const total = mfg.total_cost || ((mfg.total_green_cost || 0) + (mfg.gas_cost || 0) + (mfg.packaging_cost || 0) + (mfg.labour_cost || 0) + (mfg.overhead_cost || 0));
  const perKg = mfg.total_roasted ? total / mfg.total_roasted : 0;
  return (
    <Card title={`Manufacturing Cost — ${monthLabel}`} testId="mr-mfg-block">
      <table className="w-full text-sm">
        <tbody>
          <tr className="border-b"><td className="py-2">Green coffee (FIFO)</td><td className="py-2 text-right tabular-nums">{fmtZar(mfg.total_green_cost)}</td></tr>
          <tr className="border-b"><td className="py-2">Gas</td><td className="py-2 text-right tabular-nums">{fmtZar(mfg.gas_cost)}</td></tr>
          <tr className="border-b"><td className="py-2">Packaging</td><td className="py-2 text-right tabular-nums">{fmtZar(mfg.packaging_cost)}</td></tr>
          <tr className="border-b"><td className="py-2">Labour</td><td className="py-2 text-right tabular-nums">{fmtZar(mfg.labour_cost)}</td></tr>
          <tr className="border-b"><td className="py-2">Overheads</td><td className="py-2 text-right tabular-nums">{fmtZar(mfg.overhead_cost)}</td></tr>
          <tr className="border-b font-bold"><td className="py-2">Total</td><td className="py-2 text-right tabular-nums">{fmtZar(total)}</td></tr>
          <tr className="border-b"><td className="py-2">Roasted kg</td><td className="py-2 text-right tabular-nums">{mfg.total_roasted?.toLocaleString?.("en-ZA")}</td></tr>
          <tr className="font-bold"><td className="py-2">Cost per kg</td><td className="py-2 text-right tabular-nums">{fmtZar(perKg)}</td></tr>
        </tbody>
      </table>
    </Card>
  );
}


function AgingBlock({ aging, loading, monthLabel }) {
  return (
    <Card title={`Age Receivables — as at ${monthLabel}`} testId="mr-aging-block">
      {loading && <p className="text-sm text-muted-foreground">Fetching from Zoho invoices…</p>}
      {!loading && !aging?.available && (
        <p className="text-sm text-muted-foreground">Not available — Zoho is disconnected or has no unpaid invoices.</p>
      )}
      {aging?.available && (
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground uppercase tracking-wider border-b">
            <tr><th className="text-left py-2">Bucket</th><th className="text-right py-2">Amount</th></tr>
          </thead>
          <tbody>
            {aging.buckets.map((b) => (
              <tr key={b.label} className="border-b" data-testid={`mr-aging-${b.label}`}>
                <td className="py-2">{b.label}</td>
                <td className="py-2 text-right tabular-nums">{fmtZar(b.amount)}</td>
              </tr>
            ))}
            <tr className="border-b font-bold">
              <td className="py-2">Total</td>
              <td className="py-2 text-right tabular-nums">{fmtZar(aging.total)}</td>
            </tr>
            {aging.movement !== null && aging.movement !== undefined && (
              <tr>
                <td className="py-2 flex items-center gap-1.5">
                  {aging.movement > 0 ? <TrendingUp className="w-4 h-4 text-destructive" /> : <TrendingDown className="w-4 h-4 text-green-700" />}
                  Movement vs prior month
                </td>
                <td className={`py-2 text-right tabular-nums font-medium ${aging.movement > 0 ? "text-destructive" : "text-green-700"}`}>
                  {fmtZar(aging.movement)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </Card>
  );
}


function CashFlowBlock({ cf, monthLabel }) {
  if (!cf.available) {
    return (
      <Card title={`Cash Flow — ${monthLabel}`} testId="mr-cashflow-block">
        <p className="text-sm text-muted-foreground">Cash-flow statement not available — Zoho is disconnected or endpoint returned empty.</p>
      </Card>
    );
  }
  return (
    <Card title={`Cash Flow — ${monthLabel}`} testId="mr-cashflow-block">
      <table className="w-full text-sm">
        <tbody>
          <tr className="border-b"><td className="py-2">Opening balance</td><td className="py-2 text-right tabular-nums">{fmtZar(cf.opening_balance)}</td></tr>
          <tr className="border-b">
            <td className="py-2">Change during period</td>
            <td className={`py-2 text-right tabular-nums font-medium ${cf.change < 0 ? "text-destructive" : "text-green-700"}`}>{fmtZar(cf.change)}</td>
          </tr>
          <tr className="font-bold"><td className="py-2">Closing balance</td><td className="py-2 text-right tabular-nums">{fmtZar(cf.closing_balance)}</td></tr>
        </tbody>
      </table>
    </Card>
  );
}


function VatBlock({ vat, monthLabel }) {
  if (!vat.available) return null;
  return (
    <Card title={`VAT — ${monthLabel}`} testId="mr-vat-block">
      <table className="w-full text-sm">
        <tbody>
          <tr className="border-b"><td className="py-2">Output VAT collected</td><td className="py-2 text-right tabular-nums">{fmtZar(vat.output_vat)}</td></tr>
          <tr className="border-b"><td className="py-2">Input VAT paid</td><td className="py-2 text-right tabular-nums">{fmtZar(vat.input_vat)}</td></tr>
          <tr className="font-bold">
            <td className="py-2">Net {vat.kind}</td>
            <td className={`py-2 text-right tabular-nums ${vat.payable > 0 ? "text-destructive" : "text-green-700"}`}>{fmtZar(vat.payable)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}


function KpiCard({ label, value, tone = "", testId }) {
  return (
    <div className="card p-4" data-testid={testId}>
      <p className="label-tiny">{label}</p>
      <p className={`font-display text-2xl mt-1 tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}


function Card({ title, children, testId }) {
  return (
    <section className="card p-5" data-testid={testId}>
      <h3 className="font-display text-lg mb-3">{title}</h3>
      {children}
    </section>
  );
}
